import {
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { IcdCodeForm } from '../components/icd-codes/icd-code-form/form';
import { IcdCodeFormValues } from '../components/icd-codes/icd-code-form/validation-schema';
import { IcdCodeGrid } from '../components/icd-codes/icd-code-grid';
import {
  DynamicDrawerProvider,
  useDynamicDrawerContext,
} from '../context/dynamic-drawer.context';
import { useSnackbarContext } from '../context/snackbar.context';
import { useBillingWriteAccess } from '../hooks/use-billing-write-access';
import { useCreateIcdCodeMutation } from '../types';

export const ICD_CODES_TEST_ID = 'icd-codes-container';

const IcdCodesContent = (): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();

  const [createIcdCode] = useCreateIcdCodeMutation();

  const handleCreate = useCallback(
    async (values: IcdCodeFormValues): Promise<void> => {
      try {
        await createIcdCode({
          variables: {
            icdCodeInput: {
              indication: values.indication,
              universalCode: values.universalCode,
              rightCode: values.rightCode || null,
              leftCode: values.leftCode || null,
              startDate: dayjs(values.startDate).format('YYYY-MM-DD'),
              endDate: dayjs(values.endDate).format('YYYY-MM-DD'),
            },
          },
          refetchQueries: ['getIcdCodes'],
        });
        setSnackbarState('ICD code created successfully', 'success', 3000);
        onClose();
      } catch {
        setSnackbarState('Error creating ICD code', 'error', 3000);
      }
    },
    [createIcdCode, setSnackbarState, onClose],
  );

  const handleAddRecord = useCallback((): void => {
    setDrawerContent(
      <HHStack padding={4}>
        <IcdCodeForm onSubmit={handleCreate} />
      </HHStack>,
      <HHTypography hhVariant="h6">Add ICD Code</HHTypography>,
      undefined,
      'temporary',
    );
  }, [setDrawerContent, handleCreate]);

  return (
    <HHStack
      flex={1}
      direction="column"
      spacing={4}
      minHeight="0"
      data-testid={ICD_CODES_TEST_ID}
    >
      <HHStack
        sx={{ paddingLeft: 6, paddingRight: 6, paddingTop: 6 }}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <HHTypography hhVariant="section-title">ICD Codes</HHTypography>
        {canWrite && (
          <HHButton
            hhVariant="variant-bypass"
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={handleAddRecord}
            data-testid="add-icd-code-button"
            sx={{ minWidth: 120 }}
          >
            Add ICD Code
          </HHButton>
        )}
      </HHStack>
      <IcdCodeGrid />
    </HHStack>
  );
};

const IcdCodes = (): JSX.Element => (
  <DynamicDrawerProvider>
    <IcdCodesContent />
  </DynamicDrawerProvider>
);

export default IcdCodes;
